import {
	ReactElement,
	useCallback,
	useState
} from "react";
import {
	Dialog,
	Form,
	FormItem,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useIsTouchDevice,
	useUnmountedCancellation
} from "client/Hooks";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import { doesNotExceedLength, isNotEmpty } from "client/Validation/rules";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { IntegrationFlowCapabilityKind } from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { InputDataFormData } from "./common";

export type ConfigureInputDataDialogProps = {
	initialData: InputDataFormData;
	onClose: () => void;
	onSave: (inputData: InputDataFormData) => void;
};

export const ConfigureInputDataDialog = (props: ConfigureInputDataDialogProps): ReactElement => {
	const {
		initialData,
		onSave,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<InputDataFormData>(initialData);

	const isTouch: boolean = useIsTouchDevice();
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const formHandler: IFormHandler<InputDataFormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			try {
				const validationResult: InputValidationResult = await formHandler
					.validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				setIsBusy(false);
				onSave(formData);
				onClose();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			onClose,
			onSave
		]
	);

	return (
		<Dialog
			trackingId="integration-configure-input-data-dialog"
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={localize("_AddInputData")}
			/>
			<Dialog.Body>
				<p>{localize("_PleaseInputTheDataThatWillBeUsedAsTheValueForThePlaceholderInfo")}</p>
				<Form onSubmit={saveHandler}>
					{formHandler.when(
						hasSearchCapability,
						(attachable): ReactElement => (
							<FormItem
								label={localize("_SearchQuery")}
								isRequired
							>
								<TextInput
									{...attachable.for("searchTerm").attachToTextInput()}
									placeholder={localize("_EnterSearchQuery")}
									autoFocus={!isTouch
										? "focus"
										: undefined
									}
								/>
								<ValidationError {...attachable.for("searchTerm").attachToValidationError()} />
							</FormItem>
						)
					)}
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						onClick={saveHandler}
						text={localize("_Save")}
						trackingId="save"
					/>
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Cancel")}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
ConfigureInputDataDialog.displayName = "ConfigureInputDataDialog";

function hasSearchCapability(formData: InputDataFormData): boolean {
	return formData.capabilities
		.includes(IntegrationFlowCapabilityKind.Search);
}

const validationConfiguration: IValidationConfiguration<InputDataFormData> = createValidationConfiguration<InputDataFormData>();

validationConfiguration
	.when(hasSearchCapability)
	.forScalar("searchTerm")
	.add(
		isNotEmpty(),
		doesNotExceedLength(255)
	);

export type ConfigureInputDataDialogInitialData = Omit<
	ConfigureInputDataDialogProps,
	"onSave" | "onClose"
>;

export function useConfigureInputDataDialog(): [
	element: ReactElement | null,
	open: (props: ConfigureInputDataDialogInitialData) => Promise<TryResult<InputDataFormData>>
] {
	return useDataInputDialog(
		(props, onCancellation, onConfirmation) => (
			<ConfigureInputDataDialog
				{...props}
				onSave={onConfirmation}
				onClose={onCancellation}
			/>
		)
	);
}